import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import SugarDaddyProfileForm from '@/components/forms/SugarDaddyProfileForm';
import { useAuth } from '@/context/auth';
import { createSugarDaddyProfile } from '@/services/sugar-profile.service';
import { useToast } from '@/hooks/use-toast';
import { useLanguageUtils } from '@/context/language-context';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Terminal } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { FileWithPath } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

const BUCKET_NAME = 'sugar_media';

const CreateSugarDaddyProfilePage: React.FC = () => {
  const { t } = useTranslation(['sugar', 'forms', 'common']);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getUrlWithLanguage } = useLanguageUtils();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Upload Helper (Photos Only) ---
  const uploadPhotos = async (files: FileWithPath[]): Promise<{ url: string; name: string }[] | null> => {
      if (!user || files.length === 0) return [];
      // ... (upload logic identical to SB photos) ...
       const uploadPromises = files.map(async (file) => {
          const fileExt = file.name.split('.').pop();
          const uniqueFileName = `${uuidv4()}.${fileExt}`;
          const filePath = `public/${user.id}/photos/${uniqueFileName}`;
          const { error: uploadError } = await supabase.storage.from(BUCKET_NAME).upload(filePath, file);
          if (uploadError) {
              console.error(`Error uploading photo:`, uploadError);
              throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
          }
          const { data: urlData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(filePath);
          return { url: urlData.publicUrl, name: file.name };
      });
       try {
          return await Promise.all(uploadPromises);
      } catch (err) {
          setError((err as Error).message);
          return null;
      }
  };

  const handleSubmit = async (formData: any, filesToUpload: FileWithPath[]) => {
    if (!user) return;
    setIsSubmitting(true);
    setError(null);

    try {
        // Upload photos
        const uploadedPhotos = await uploadPhotos(filesToUpload);

        if (uploadedPhotos === null) {
            setIsSubmitting(false);
            return; // Upload error occurred
        }

        // Prepare profile data with photo URLs
        const profileData = {
            ...formData,
            photos: uploadedPhotos.length > 0 
                ? { gallery: uploadedPhotos.map(p => p.url), visibility: 'public' } // Default visibility
                : undefined,
        };

        const result = await createSugarDaddyProfile(user.id, profileData);

        if (result.success) {
            toast({ title: t('forms:success_profile_created') });
            navigate(getUrlWithLanguage('/sugar-relationships'));
        } else {
            setError(result.error?.message || t('common:error_unknown'));
            toast({ title: t('common:error_title'), description: error, variant: 'destructive' });
        }
    } catch (err: any) {
        console.error("Submission error:", err);
        setError(err.message || t('common:error_unexpected'));
        toast({ title: t('common:error_title'), description: error, variant: 'destructive' });
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto py-8 max-w-2xl">
        <h1 className="text-2xl font-bold mb-6">{t('sugar:title_create_sd_profile')}</h1>
         {error && (
           <Alert variant="destructive" className="mb-4">
             <Terminal className="h-4 w-4" />
             <AlertTitle>{t('common:error_submission')}</AlertTitle>
             <AlertDescription>{error}</AlertDescription>
           </Alert>
        )}
        <SugarDaddyProfileForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
      </div>
    </Layout>
  );
};

export default CreateSugarDaddyProfilePage; 